import { FileUp, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { CampaignAnswerDraft, CampaignQuestion } from '@/types/campaign'

const MAX_SHORT_TEXT = 255
const MAX_LONG_TEXT = 5000
// Mesmos limites do backend (campaign-kind.ts)
export const CAMPAIGN_FILE_MAX_SIZE = 10 * 1024 * 1024
const FILE_ACCEPT =
	'.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp,application/pdf'
// Acima disso a lista de opções vira um dropdown
const MAX_RADIO_OPTIONS = 5

interface CampaignQuestionInputProps {
	question: CampaignQuestion
	value: CampaignAnswerDraft | undefined
	onChange: (value: CampaignAnswerDraft) => void
	onFileError: (message: string) => void
}

const choiceButtonClass = (selected: boolean) =>
	cn(
		'rounded-md border px-3 py-1.5 text-sm transition-colors',
		selected
			? 'border-kaefer-sapphire-blue bg-kaefer-sapphire-blue text-white'
			: 'border-kaefer-light-silver bg-white text-kaefer-dark-grey hover:bg-kaefer-light-silver/40',
	)

export function CampaignQuestionInput({
	question,
	value,
	onChange,
	onFileError,
}: CampaignQuestionInputProps) {
	const { t } = useTranslation()

	switch (question.type) {
		case 'SHORT_TEXT':
			return (
				<Input
					value={value?.text ?? ''}
					maxLength={MAX_SHORT_TEXT}
					onChange={(e) => onChange({ text: e.target.value })}
				/>
			)
		case 'LONG_TEXT':
			return (
				<div>
					<Textarea
						rows={4}
						value={value?.text ?? ''}
						maxLength={MAX_LONG_TEXT}
						onChange={(e) => onChange({ text: e.target.value })}
					/>
					<p className="mt-1 text-right text-xs text-muted-foreground">
						{(value?.text ?? '').length}/{MAX_LONG_TEXT}
					</p>
				</div>
			)
		case 'BOOLEAN':
			return (
				<div className="flex gap-2">
					{[true, false].map((option) => (
						<button
							key={String(option)}
							type="button"
							className={choiceButtonClass(value?.boolean === option)}
							onClick={() => onChange({ boolean: option })}
						>
							{t(option ? 'campaigns.respond.yes' : 'campaigns.respond.no')}
						</button>
					))}
				</div>
			)
		case 'SINGLE_CHOICE':
			return question.options.length > MAX_RADIO_OPTIONS ? (
				<NativeSelect
					value={value?.text ?? ''}
					onChange={(e) => onChange({ text: e.target.value })}
				>
					<option value="">{t('campaigns.respond.select')}</option>
					{question.options.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</NativeSelect>
			) : (
				<div className="flex flex-col gap-2">
					{question.options.map((option) => (
						<label
							key={option}
							className="flex cursor-pointer items-center gap-2 text-sm text-kaefer-dark-grey"
						>
							<input
								type="radio"
								name={question.id}
								className="size-4 accent-kaefer-sapphire-blue"
								checked={value?.text === option}
								onChange={() => onChange({ text: option })}
							/>
							{option}
						</label>
					))}
				</div>
			)
		case 'MULTIPLE_CHOICE': {
			const selected = value?.choices ?? []
			return (
				<div className="flex flex-col gap-2">
					{question.options.map((option) => (
						<label
							key={option}
							className="flex cursor-pointer items-center gap-2 text-sm text-kaefer-dark-grey"
						>
							<input
								type="checkbox"
								className="size-4 accent-kaefer-sapphire-blue"
								checked={selected.includes(option)}
								onChange={(e) =>
									onChange({
										choices: e.target.checked
											? [...selected, option]
											: selected.filter((item) => item !== option),
									})
								}
							/>
							{option}
						</label>
					))}
				</div>
			)
		}
		case 'SCALE': {
			const min = question.scaleMin ?? 1
			const max = question.scaleMax ?? 5
			return (
				<div className="flex flex-wrap gap-2">
					{Array.from({ length: max - min + 1 }, (_, index) => min + index).map(
						(option) => (
							<button
								key={option}
								type="button"
								className={cn(
									choiceButtonClass(value?.number === option),
									'min-w-10 text-center',
								)}
								onClick={() => onChange({ number: option })}
							>
								{option}
							</button>
						),
					)}
				</div>
			)
		}
		case 'FILE':
			return (
				<div className="flex flex-wrap items-center gap-2">
					<label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-kaefer-light-silver bg-white px-3 py-1.5 text-sm text-kaefer-dark-grey hover:bg-kaefer-light-silver/40">
						<FileUp className="size-4" />
						{value?.file
							? t('campaigns.respond.changeFile')
							: t('campaigns.respond.chooseFile')}
						<input
							type="file"
							className="sr-only"
							accept={FILE_ACCEPT}
							onChange={(e) => {
								const file = e.target.files?.[0]
								e.target.value = ''
								if (!file) return
								if (file.size > CAMPAIGN_FILE_MAX_SIZE) {
									onFileError(t('campaigns.respond.fileTooLarge'))
									return
								}
								onChange({ file })
							}}
						/>
					</label>
					{value?.file ? (
						<span className="flex items-center gap-1 text-sm text-kaefer-dark-grey">
							<span className="max-w-56 truncate">{value.file.name}</span>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-6 w-auto"
								onClick={() => onChange({})}
							>
								<X className="size-3.5" />
							</Button>
						</span>
					) : (
						<span className="text-xs text-muted-foreground">
							{t('campaigns.respond.maxFile')}
						</span>
					)}
				</div>
			)
	}
}
